import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Field, FieldProps, Formik, FormikProps } from 'formik';
import { boolean as booleanYup, object as objectYup, string as stringYup } from 'yup';
import getValue from 'lodash/get';
import useInitialErrors from '../../../hooks/formik-initial-errors';
import { ManagerFormsType, ManagerRequestType, SocialEnum } from '../../../types/auth';
import { authService } from '../../../lib/api/auth';
import { deleteReduxProfile } from '../../../redux/registration';
import { goToRoom } from './../utils';
import ReCAPTCHA from 'react-google-recaptcha';
import { getSiteKey, isConsentFeatureEnabled } from '../../../lib/utils/common-functions';
import { registrationLocationConstants } from '../../../lib/constants/constants';
import CountryDropdown from '../../../components/dropdown/country';
import StateDropdown from '../../../components/dropdown/states';
import CityDropdown from '../../../components/dropdown/city';
import websitelogo from '../../../assets/img/offerx-logo.svg';
import LegalDocumentModal from '../../room/components/LegalDocumentModal';
import { Link, useNavigate } from 'react-router-dom';
import { routes } from '../../routes/routes-names';
import { getConsentWorkflowSettings } from '../../../redux/consentWorkFlow';
import {
    sessionStorageClearAll,
    sessionStorageGetItem,
    sessionStorageRemoveItem,
    sessionStorageSetItem,
} from '../../../components/storage/sessionStorageManager';
import { localStorageGetItem, localStorageRemoveItem } from '../../../components/storage/localStorageManager';
import { useConsentFlow, useUserProfile } from '../../../hooks/useSelector';
import { useAppDispatch } from '../../../redux/reduxhooks';
import { isCaptchaEnabled } from '../../../config/constants';

type Props = {
    managerId?: string;
    handleSelect: (key: string, key2: string) => void;
};

const initialFormData = {
    CompanyAddress: '',
    country: '',
    state: '',
    CityDistrict: '',
    PostalCode: '',
    IsCompanyTermsChecked: false,
    recaptcha: '',
    // AddressLine: '',
    Address2: '',
    OwnerPhoneNumber: '',
    ReferralCode: '',
    CompCountryCode: '',
    OwnerCountryCode: '',
};

const ManagerStep3: React.FC<Props> = ({ managerId, handleSelect }) => {
    const dispatch = useAppDispatch();
    const consentWorkFlowSettings = useConsentFlow();
    const reduxProfile = useUserProfile();
    const [sessionData, setSessionData] = useState<any>();
    const [sessionDataStep3, setSessionDataStep3] = useState<any>();
    const [stateDisabled, setStateDisabled] = useState(true);
    const [cityDisabled, setCityDisabled] = useState(true);
    const [iso2CountryString, setIso2CountryString] = useState('');
    const [iso2StateString, setIso2StateString] = useState('');
    const [passString, setPassString] = useState('');
    const [isStateValueAvailable, setIsStateValueAvailable] = useState(true);
    const [isCityValueAvailable, setIsCityValueAvailable] = useState(true);

    const captchaEnabled = String(isCaptchaEnabled).toLowerCase() === 'true' ? true : false;

    const navigate = useNavigate();

    useEffect(() => {
        if (!consentWorkFlowSettings?.settings) dispatch(getConsentWorkflowSettings());

        let data: any;
        data = sessionStorageGetItem('company-basic-details', false)!;
        const data2 = sessionStorageGetItem('managerStep3', false)!;

        delete data2?.recaptcha;
        setSessionData(data);
        setSessionDataStep3(data2);

        setSelectedCountryId(data2?.country);

        const CustomAuthPass = localStorageGetItem('Password')!;

        if (CustomAuthPass) {
            setPassString(CustomAuthPass);
        }

        window.onbeforeunload = () => {
            sessionStorageRemoveItem('company-basic-details');
            sessionStorageRemoveItem('managerStep3');
        };
    }, []);

    const sessionFormData = useMemo(
        () =>
            sessionData
                ? {
                      ...initialFormData,
                      FirstName: sessionData?.FirstName,
                      LastName: sessionData?.LastName,
                      Email: sessionData?.Email,
                      CompanyName: sessionData?.CompanyName,
                      GstNumber: sessionData?.GstNumber,
                      CompanyEmail: sessionData?.CompanyEmail,
                      LinkedINUrl: sessionData?.LinkedINUrl,
                      Phone: sessionData?.Phone,
                      cinNumber: sessionData?.cinNumber,
                      Website: sessionData?.Website,
                      MiddleName: sessionData?.MiddleName,
                      CityDistrict: sessionData?.CityDistrict,
                      state: sessionData?.state,
                      country: sessionData?.country,
                      ReferralCode: sessionData?.ReferralCode,
                      OwnerPhoneNumber: sessionData?.OwnerPhoneNumber,
                      OwnerCountryCode: sessionData?.OwnerCountryCode,
                      CompCountryCode: sessionData?.CompCountryCode,
                  }
                : initialFormData,
        [sessionData]
    );

    const sessionFormDataStep3 = useMemo(
        () =>
            sessionDataStep3
                ? {
                      ...sessionData,
                      CompanyAddress: sessionDataStep3?.CompanyAddress,
                      Address2: sessionDataStep3?.Address2,
                      country: sessionDataStep3?.country,
                      state: sessionDataStep3?.state,
                      CityDistrict: sessionDataStep3?.CityDistrict,
                      PostalCode: sessionDataStep3?.PostalCode,
                      IsCompanyTermsChecked: sessionDataStep3?.IsCompanyTermsChecked,
                      recaptcha: sessionDataStep3?.recaptcha,
                      ReferralCode: sessionDataStep3?.ReferralCode,
                  }
                : initialFormData,
        [sessionDataStep3, sessionData]
    );

    const initialErrors = useInitialErrors(
        sessionFormData,
        getValidationSchema(isStateValueAvailable, isCityValueAvailable, captchaEnabled)
    );

    const submitForm = useCallback(
        async (values: ManagerFormsType) => {
            const addressData = values.CompanyAddress + '%' + values.Address2;

            // if (!reduxProfile) return;

            const requestData: ManagerRequestType = {
                ...values,
                Phone: values.Phone,
                CityDistrict: values.CityDistrict === '' ? null : values.CityDistrict,
                state: values.state === '' ? null : values.state,
                CompanyAddress: addressData,
                OwnerPhoneNumber: values.OwnerPhoneNumber,
                GstNumber: values.GstNumber === '' ? null : values.GstNumber,
                AuthType: passString ? SocialEnum.CustomAuth : reduxProfile?.authType!,
                Token: reduxProfile?.token! || '',
                Password: (passString && passString) || '',
            };

            try {
                const data = await authService.managerSignUp(requestData);
                if (data) {
                    dispatch(deleteReduxProfile());
                    localStorageRemoveItem('Password');
                    localStorageRemoveItem('Email');
                    await goToRoom(data, dispatch, navigate);
                    sessionStorageClearAll();
                }
            } catch (err) {}
        },
        [passString, reduxProfile?.authType, reduxProfile?.token, dispatch]
    );

    // Join company by invitation
    useEffect(() => {
        if (!managerId || !reduxProfile) return;
        (async function () {
            try {
                await authService.managerJoinByInvitation({
                    Email: reduxProfile.email,
                    InvitationId: managerId,
                });
                const data = await authService.socialSignIn({
                    Email: reduxProfile.email,
                    AuthType: reduxProfile.authType,
                    Token: reduxProfile.token,
                    Location: registrationLocationConstants.IsSocial,
                });
                if (data) {
                    dispatch(deleteReduxProfile());
                    await goToRoom(data, dispatch, navigate);
                }
            } catch (err) {}
        })();
    }, [managerId, dispatch, reduxProfile]);

    const [openLegalDocModal, setOpenLegalDocModal] = useState(false);
    const [selectedCountryId, setSelectedCountryId] = useState('');
    const ref = useRef(null);

    const onCountrySelectionChange = (countryId: string) => {
        setSelectedCountryId(countryId);
    };

    const displayCountryPolicyDocument = (e: any) => {
        e.preventDefault();
        e.stopPropagation();

        // if (ref?.current && (ref?.current as any)?.values?.country) {
        //     setSelectedCountryId((ref.current as any)?.values?.country);
        //     setOpenLegalDocModal(true);
        // }
        if (selectedCountryId) setOpenLegalDocModal(true);
        else setOpenLegalDocModal(false);
    };

    return (
        <>
            <Formik
                innerRef={ref}
                onSubmit={submitForm}
                enableReinitialize
                initialValues={sessionDataStep3 !== null ? sessionFormDataStep3 : sessionFormData}
                validationSchema={getValidationSchema(isStateValueAvailable, isCityValueAvailable, captchaEnabled)}
                initialErrors={initialErrors}
            >
                {(formikProps: FormikProps<ManagerFormsType>) => {
                    const { handleSubmit, values, isSubmitting, setFieldTouched, setFieldValue } = formikProps;
                    const siteKey = getSiteKey();

                    return (
                        <form onSubmit={handleSubmit}>
                            {/* <div
                            className="tab-pane fade"
                            id="nav-contact"
                            role="tabpanel"
                            aria-labelledby="nav-contact-tab"
                        > */}
                            <div className="card lt-shadow-tin border-default border-radius-12 mb-4">
                                <div className="card-body">
                                    <div className="container-fluid">
                                        <div className="row mb-4">
                                            <div className="col-md-12">
                                                <img
                                                    loading="lazy"
                                                    src={websitelogo}
                                                    className="mx-auto d-block mb-2"
                                                    width={120}
                                                    alt="Logo"
                                                />
                                                <div className="fs-18 fw-600 text-center">Sign Up</div>
                                            </div>
                                        </div>
                                        <div className="row">
                                            <div className="col-md-12">
                                                <div className="fs-18 fw-600 mb-3">Company location</div>
                                            </div>
                                            <div className="col-md-12">
                                                <div className="mb-4">
                                                    <label className="fs-14 fw-400 mb-2">
                                                        Country <span className="lt-text-error">*</span>
                                                    </label>
                                                    <Field name="country">
                                                        {(fieldProps: FieldProps) => {
                                                            const { field, form } = fieldProps;
                                                            const error =
                                                                getValue(form.touched, field.name) &&
                                                                getValue(form.errors, field.name);

                                                            return (
                                                                <>
                                                                    <CountryDropdown
                                                                        id="country"
                                                                        setFieldValue={setFieldValue}
                                                                        countryFieldValue={field.value}
                                                                        setStateDisabled={setStateDisabled}
                                                                        setCityDisabled={setCityDisabled}
                                                                        setIso2CountryString={setIso2CountryString}
                                                                        onCountrySelectionChange={
                                                                            onCountrySelectionChange
                                                                        }
                                                                    />

                                                                    {typeof error === 'string' && (
                                                                        <small className="text-danger fs-12">
                                                                            {' '}
                                                                            {field.value === '' ? error : ''}
                                                                        </small>
                                                                    )}
                                                                </>
                                                            );
                                                        }}
                                                    </Field>
                                                </div>
                                            </div>
                                            <div className="col-md-12">
                                                <div className="mb-4">
                                                    <label className="fs-14 fw-400 mb-2">
                                                        Flat / Building <span className="lt-text-error">*</span>
                                                    </label>
                                                    <Field name="CompanyAddress">
                                                        {(fieldProps: FieldProps) => {
                                                            const { field, form } = fieldProps;
                                                            const error =
                                                                getValue(form.touched, field.name) &&
                                                                getValue(form.errors, field.name);
                                                            return (
                                                                <>
                                                                    <input
                                                                        type="text"
                                                                        value={field.value}
                                                                        onChange={(ev) => {
                                                                            const forbiddenPattern = /%/;

                                                                            if (
                                                                                !forbiddenPattern.test(ev.target.value)
                                                                            ) {
                                                                                setFieldTouched(field.name);
                                                                                setFieldValue(
                                                                                    field.name,
                                                                                    ev.target.value
                                                                                );
                                                                            }
                                                                        }}
                                                                        className={`form-control ${
                                                                            error ? 'is-invalid' : ''
                                                                        }`}
                                                                        placeholder="Enter Flat / Building"
                                                                        maxLength={250}
                                                                    />
                                                                    {typeof error === 'string' && (
                                                                        <small className="text-danger fs-12">
                                                                            {error}
                                                                        </small>
                                                                    )}
                                                                    {/* <div className="d-flex justify-content-between mt-2">
                                                                        <small className="fs-12">{field.value.length}/250</small>
                                                                    </div> */}
                                                                </>
                                                            );
                                                        }}
                                                    </Field>
                                                </div>
                                            </div>
                                            <div className="col-md-12">
                                                <div className="mb-4">
                                                    <label className="fs-14 fw-400 mb-2">
                                                        Street Address <span className="lt-text-error">*</span>
                                                    </label>
                                                    <Field name="Address2">
                                                        {(fieldProps: FieldProps) => {
                                                            const { field, form } = fieldProps;
                                                            const error =
                                                                getValue(form.touched, field.name) &&
                                                                getValue(form.errors, field.name);
                                                            return (
                                                                <>
                                                                    <input
                                                                        type="text"
                                                                        value={field.value}
                                                                        onChange={(ev) => {
                                                                            const forbiddenPattern = /%/;

                                                                            if (
                                                                                !forbiddenPattern.test(ev.target.value)
                                                                            ) {
                                                                                setFieldTouched(field.name);
                                                                                setFieldValue(
                                                                                    field.name,
                                                                                    ev.target.value
                                                                                );
                                                                            }
                                                                        }}
                                                                        className={`form-control ${
                                                                            error ? 'is-invalid' : ''
                                                                        }`}
                                                                        placeholder="Enter Street Address"
                                                                        maxLength={250}
                                                                    />
                                                                    {typeof error === 'string' && (
                                                                        <small className="text-danger fs-12">
                                                                            {error}
                                                                        </small>
                                                                    )}
                                                                    {/* <div className="d-flex justify-content-between mt-2">
                                                                        <small className="fs-12">{field.value.length}/250</small>
                                                                    </div> */}
                                                                </>
                                                            );
                                                        }}
                                                    </Field>
                                                </div>
                                            </div>
                                            <div className="col-md-12">
                                                <div className="mb-4">
                                                    <label className="fs-14 fw-400 mb-2">
                                                        State <span className="lt-text-error">*</span>
                                                    </label>
                                                    <Field name="state">
                                                        {(fieldProps: FieldProps) => {
                                                            const { field, form } = fieldProps;
                                                            const error =
                                                                getValue(form.touched, field.name) &&
                                                                getValue(form.errors, field.name);
                                                            return (
                                                                <>
                                                                    <StateDropdown
                                                                        id="state"
                                                                        setFieldValue={setFieldValue}
                                                                        stateFieldValue={field.value}
                                                                        setIsStateValueAvailable={
                                                                            setIsStateValueAvailable
                                                                        }
                                                                        setIsCityValueAvailable={
                                                                            setIsCityValueAvailable
                                                                        }
                                                                        setStateDisabled={setStateDisabled}
                                                                        stateDisabled={stateDisabled}
                                                                        setCityDisabled={setCityDisabled}
                                                                        Iso2CountryString={iso2CountryString}
                                                                        setIso2StateString={setIso2StateString}
                                                                    />

                                                                    {typeof error === 'string' && (
                                                                        <small className="text-danger fs-12">
                                                                            {' '}
                                                                            {isStateValueAvailable && field.value === ''
                                                                                ? error
                                                                                : ''}
                                                                        </small>
                                                                    )}
                                                                </>
                                                            );
                                                        }}
                                                    </Field>
                                                </div>
                                            </div>
                                            <div className="col-md-12">
                                                <div className="mb-4">
                                                    <label className="fs-14 fw-400 mb-2">
                                                        City <span className="lt-text-error">*</span>
                                                    </label>
                                                    <Field name="CityDistrict">
                                                        {(fieldProps: FieldProps) => {
                                                            const { field, form } = fieldProps;
                                                            const error =
                                                                getValue(form.touched, field.name) &&
                                                                getValue(form.errors, field.name);
                                                            return (
                                                                <>
                                                                    <CityDropdown
                                                                        id="city"
                                                                        setFieldValue={setFieldValue}
                                                                        cityFieldValue={field.value}
                                                                        setIsCityValueAvailable={
                                                                            setIsCityValueAvailable
                                                                        }
                                                                        setCityDisabled={setCityDisabled}
                                                                        cityDisabled={cityDisabled}
                                                                        iso2CountryString={iso2CountryString}
                                                                        iso2StateString={iso2StateString}
                                                                    />

                                                                    {typeof error === 'string' && (
                                                                        <small className="text-danger fs-12">
                                                                            {' '}
                                                                            {isCityValueAvailable && field.value === ''
                                                                                ? error
                                                                                : ''}
                                                                        </small>
                                                                    )}
                                                                </>
                                                            );
                                                        }}
                                                    </Field>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="col-md-12">
                                            <div className="mb-4">
                                                <label className="fs-14 fw-400 mb-2">
                                                    Postal / Zip Code <span className="lt-text-error">*</span>
                                                </label>
                                                <Field name="PostalCode">
                                                    {(fieldProps: FieldProps) => {
                                                        const { field, form } = fieldProps;
                                                        const error =
                                                            getValue(form.touched, field.name) &&
                                                            getValue(form.errors, field.name);
                                                        return (
                                                            <>
                                                                <input
                                                                    type="text"
                                                                    value={field.value}
                                                                    onChange={(ev) => {
                                                                        setFieldTouched(field.name);
                                                                        setFieldValue(field.name, ev.target.value);
                                                                    }}
                                                                    className={`form-control ${
                                                                        error ? 'is-invalid' : ''
                                                                    }`}
                                                                    placeholder="Enter Postal / Zip Code"
                                                                    maxLength={10}
                                                                />
                                                                {typeof error === 'string' && (
                                                                    <small className="text-danger fs-12">{error}</small>
                                                                )}
                                                            </>
                                                        );
                                                    }}
                                                </Field>
                                            </div>
                                        </div>
                                        <div className="col-md-12">
                                            <div className="mb-4">
                                                <label className="fs-14 fw-400 mb-2">
                                                    Referral Code{' '}
                                                    <i className="bi bi-info-circle lt-tooltip">
                                                        <span className="lt-tooltiptext lt-shadow-md fw-400 fs-14 bottom-tip">
                                                            A referral code is a unique identifier that grants <br />{' '}
                                                            exclusive benefits to the person who owns the <br /> code
                                                            when it is entered during the signup process.
                                                        </span>
                                                    </i>
                                                </label>
                                                <Field name="ReferralCode">
                                                    {(fieldProps: FieldProps) => {
                                                        const { field, form } = fieldProps;
                                                        const error =
                                                            getValue(form.touched, field.name) &&
                                                            getValue(form.errors, field.name);
                                                        return (
                                                            <>
                                                                <input
                                                                    type="text"
                                                                    value={field.value}
                                                                    onChange={(ev) => {
                                                                        setFieldTouched(field.name);
                                                                        setFieldValue(field.name, ev.target.value);
                                                                    }}
                                                                    className={`form-control ${
                                                                        error ? 'is-invalid' : ''
                                                                    }`}
                                                                    placeholder="Enter Referral Code (Optional)"
                                                                    maxLength={50}
                                                                />
                                                                {typeof error === 'string' && (
                                                                    <small className="text-danger fs-12">{error}</small>
                                                                )}
                                                            </>
                                                        );
                                                    }}
                                                </Field>
                                            </div>
                                        </div>
                                        <div className="col-md-12">
                                            <div className="form-check my-2 custom-h-40">
                                                <Field
                                                    disabled={
                                                        isConsentFeatureEnabled(consentWorkFlowSettings) &&
                                                        !values.country
                                                    }
                                                    className="form-check-input cursor-pointer"
                                                    name="IsCompanyTermsChecked"
                                                    type="checkbox"
                                                    id="check"
                                                    checked={values.IsCompanyTermsChecked}
                                                ></Field>

                                                {isConsentFeatureEnabled(consentWorkFlowSettings) ? (
                                                    <label
                                                        className="form-check-label fs-13 mt-1 cursor-pointer lt-text-secondary"
                                                        htmlFor="check"
                                                    >
                                                        I have read and agree to the{' '}
                                                        <a href="" onClick={displayCountryPolicyDocument}>
                                                            Privacy Policies,
                                                            {''}
                                                            Terms & Conditions
                                                        </a>
                                                        .
                                                    </label>
                                                ) : (
                                                    <label
                                                        className="form-check-label fs-13 mt-1 cursor-pointer lt-text-secondary"
                                                        htmlFor="check"
                                                    >
                                                        I have read and agree to the{' '}
                                                        <Link to={routes.privacy} target="_blank">
                                                            Privacy Policies
                                                        </Link>{' '}
                                                        and{' '}
                                                        <Link to={routes.terms} target="_blank">
                                                            Terms & Conditions
                                                        </Link>
                                                        .
                                                    </label>
                                                )}

                                                <Field name="IsCompanyTermsChecked">
                                                    {(fieldProps: FieldProps) => {
                                                        const { field, form } = fieldProps;
                                                        const error =
                                                            getValue(form.touched, field.name) &&
                                                            getValue(form.errors, field.name);
                                                        return (
                                                            <div>
                                                                {typeof error === 'string' && (
                                                                    <small className="text-danger fs-12">{error}</small>
                                                                )}
                                                            </div>
                                                        );
                                                    }}
                                                </Field>
                                            </div>
                                        </div>
                                        {captchaEnabled && (
                                            <Field name="recaptcha">
                                                {(fieldProps: FieldProps) => {
                                                    const { field, form } = fieldProps;
                                                    const error =
                                                        getValue(form.touched, field.name) &&
                                                        getValue(form.errors, field.name);
                                                    return (
                                                        <div>
                                                            <ReCAPTCHA
                                                                sitekey={siteKey}
                                                                onChange={(value) => {
                                                                    setFieldTouched(field.name);
                                                                    setFieldValue(field.name, value);
                                                                }}
                                                            />
                                                            {typeof error === 'string' && (
                                                                <small className="text-danger fs-12">{error}</small>
                                                            )}
                                                        </div>
                                                    );
                                                }}
                                            </Field>
                                        )}
                                    </div>
                                    <div className="my-3 d-flex justify-content-between">
                                        <button
                                            onClick={() => {
                                                handleSelect('basic-details', 'company');
                                                sessionStorageSetItem('managerStep3', values, false);
                                            }}
                                            className="btn btn-outline-primary reg-next-button me-3"
                                        >
                                            Back
                                        </button>
                                        <button
                                            type="submit"
                                            disabled={isSubmitting}
                                            className="btn btn-primary reg-next-button"
                                        >
                                            {isSubmitting && <span className="spinner-border spinner-border-sm" />}Join
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </form>
                    );
                }}
            </Formik>
            {openLegalDocModal ? (
                <LegalDocumentModal
                    openLegalDocModal={openLegalDocModal}
                    setOpenLegalDocModal={setOpenLegalDocModal}
                    documentType="DEFAULT-PRIVATE"
                    countryId={selectedCountryId}
                />
            ) : (
                ''
            )}
        </>
    );
};

export default ManagerStep3;

const getValidationSchema = (isStateValueAvailable: boolean, isCityValueAvailable: boolean, captchaEnabled: boolean) =>
    objectYup().shape({
        CompanyAddress: stringYup().required('Flat / Building is required.'),
        Address2: stringYup().required('Street address is required.'),
        PostalCode: stringYup().required('Postal / Zip code is required.'),
        agreement: booleanYup().oneOf([true]),
        IsCompanyTermsChecked: booleanYup().oneOf([true], 'Please accept terms and conditions to register.'),
        recaptcha: captchaEnabled ? stringYup().nullable().required('Captcha is required.') : stringYup().nullable(),
        country: stringYup().required('Country is required.'),
        CityDistrict: isCityValueAvailable ? stringYup().required('City is required.') : stringYup(),
        state: isStateValueAvailable ? stringYup().required('State is required.') : stringYup(),
    });
